import type { ReactiveController } from 'lit';
import type { AscBreadcrumbs } from './asc-breadcrumbs';

interface AscBreadcrumbItemLike extends HTMLElement {
  active: boolean;
}

export class BreadcrumbsController implements ReactiveController {
  private _host: AscBreadcrumbs;
  private _items: AscBreadcrumbItemLike[] = [];

  /** Items currently rendered in the visible list (subset when truncating). */
  visibleItems: AscBreadcrumbItemLike[] = [];
  /** Index in visibleItems at which the static ellipsis should be injected. */
  ellipsisIndex = 0;
  /** Whether the static ellipsis indicator should be rendered. */
  showEllipsis = false;

  constructor(host: AscBreadcrumbs) {
    this._host = host;
    host.addController(this);
  }

  hostConnected() {}
  hostDisconnected() {}

  onSlotChange(_e: Event): void {
    // Query host children directly rather than slot.assignedElements(). The default slot
    // fires a second slotchange when items move to named slots (slot="item-N"), at which
    // point assignedElements() returns [] and would incorrectly reset _items to empty.
    this._items = Array.from(this._host.children).filter(
      (el): el is AscBreadcrumbItemLike => el.tagName === 'ASC-BREADCRUMB-ITEM',
    );

    // Assign named slots so the host can project each item into its own <li>
    this._items.forEach((item, i) => {
      item.dataset['slotIndex'] = String(i);
      item.slot = `item-${i}`;
    });

    this._activateLastItem();
    this.recompute();
  }

  private _activateLastItem(): void {
    this._items.forEach((item, i) => {
      item.active = i === this._items.length - 1;
    });
  }

  recompute(): void {
    const { maxAncestors, truncateType } = this._host;
    const ancestors = this._items.slice(0, -1); // all except the active (last) item
    const activeItem = this._items[this._items.length - 1];

    if (!activeItem || ancestors.length <= maxAncestors) {
      this.visibleItems = [...this._items];
      this.showEllipsis = false;
      this._host.requestUpdate();
      return;
    }

    if (truncateType === 'depth') {
      // Keep first maxAncestors from root; hide the rest before the active item
      const visibleAncestors = ancestors.slice(0, maxAncestors);
      this.visibleItems = [...visibleAncestors, activeItem];
      this.ellipsisIndex = visibleAncestors.length;
    } else {
      // 'length': keep the last N ancestors before active; always show root (index 0)
      const root = ancestors[0];
      const tail = ancestors.slice(ancestors.length - maxAncestors + 1); // N-1 closest ancestors
      const visibleAncestors =
        root && tail[0] !== root
          ? [root, ...tail]
          : ancestors.slice(ancestors.length - maxAncestors);
      this.visibleItems = [...visibleAncestors, activeItem];
      this.ellipsisIndex = 1; // ellipsis always sits right after root in length mode
    }

    this.showEllipsis = true;
    this._host.requestUpdate();
  }
}
